import { AccountPhoneNumberStatusesEnum } from "dash-common";
import { Box, ViewerContext } from "dash-ui";
import Container from "flow-components/Container/Container";
import Footer from "flow-components/Footer/Footer";
import Header from "flow-components/Header/Header";
import { useContext } from "react";
import { Navigate } from "react-router-dom";
import { RoutesPath } from "../routes";

// What permissions the user needs to access these routes.
type RoutePermissions = {
  account: boolean;
  verifiedAccount: boolean;
  hasSubscription: boolean;
};

type IProtectedRouteProps = {
  children: JSX.Element;
  customPermissions?: RoutePermissions;
  redirectUrl?: string;
  showContent?: boolean;
  withContainer?: boolean;
  withProfileDropdown?: boolean;
  withBreadcrumbs?: boolean;
  withSidebar?: boolean;
};

function ProtectedRoute({
  children,
  customPermissions,
  showContent,
  withContainer = true,
  withProfileDropdown,
  withSidebar = true,
  withBreadcrumbs = true,
}: IProtectedRouteProps): JSX.Element | null {
  const { account, broker, subscription } = useContext(ViewerContext);

  // Load permissions.
  // If no custom permissions were provided, enforce all protections.
  const permissions: RoutePermissions = customPermissions || {
    account: true,
    hasSubscription: true,
    verifiedAccount: true,
  };
  const isAccountVerified =
    account?.phone?.status === AccountPhoneNumberStatusesEnum.VERIFIED;

  if (permissions.account && !account) {
    const redirectUrl = `${RoutesPath.SignIn}?redirect=${encodeURIComponent(
      location.pathname + location.search
    )}`;
    return <Navigate to={redirectUrl} replace />;
  }

  if (permissions.verifiedAccount && !isAccountVerified) {
    if (account?.ssoId && !broker) {
      return <Navigate to={RoutesPath.CompleteSignUpSSO} />;
    }
    return <Navigate to={RoutesPath.CompleteSignUpCodeConfirmation} />;
  }

  if (permissions.hasSubscription && !subscription) {
    return <Navigate to={RoutesPath.CompleteSignUpPickSubscription} />;
  }

  if (!withContainer) {
    return <>{children}</>;
  }

  return (
    <Box h="100%" className="bg-slate-50">
      <Header withProfileDropdown={withProfileDropdown} />
      <Container
        showContent={showContent}
        withBreadcrumbs={withBreadcrumbs}
        withSidebar={withSidebar}>
        {children}
      </Container>
      <Footer variation="light" />
    </Box>
  );
}

export default ProtectedRoute;
